import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User as UserIcon, CheckCircle2, Eye, EyeOff, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Extract clean text from FastAPI error detail (string, array, or object)
function extractErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return first.msg;
  }
  if (detail?.msg) return detail.msg;
  if (err?.message === 'Network Error') {
    return 'Tidak bisa terhubung ke server. Cek koneksi internet.';
  }
  if (err?.response?.status === 401) return 'Username atau password salah';
  if (err?.response?.status === 403) return 'Akses ditolak';
  return 'Login gagal — coba lagi';
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const trimmedUser = username.trim();
  const trimmedPw = password.trim();
  const canSubmit = trimmedUser.length > 0 && trimmedPw.length > 0 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const u = await login(trimmedUser, trimmedPw);
      toast.success(`Selamat datang, ${u.full_name || u.username}`);
      const dest = u.home_route ? u.home_route
        : u.role === 'admin' ? '/dashboard'
        : u.role === 'sales' ? '/sales/catalog'
        : u.role === 'kasir' ? '/kasir'
        : u.opname_enabled ? '/stock-opname'
        : '/kasir';
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* ============ LEFT: brand panel (hidden on mobile behind form) ============ */}
      <aside className="relative overflow-hidden bg-emerald-800 text-white p-8 md:p-12 flex flex-col justify-center w-full lg:w-1/2 lg:min-h-screen order-2 lg:order-1">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 via-emerald-800 to-emerald-950" />
        <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-lime-400/10 blur-3xl" aria-hidden />
        <div className="absolute bottom-10 left-20 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" aria-hidden />
        <div className="absolute top-1/3 right-10 w-56 h-56 rounded-full bg-lime-300/10 blur-2xl" aria-hidden />
        <div className="absolute inset-0 dot-grid opacity-25" />

        <div className="relative z-10 max-w-lg mx-auto w-full">
          <img
            src="/assets/logo.png"
            alt="Main Bulutangkis"
            className="w-56 h-auto mb-8 drop-shadow-2xl brightness-110"
            draggable="false"
          />

          <h2 className="font-heading text-5xl md:text-6xl font-bold leading-tight tracking-tight mb-3">
            <span className="text-lime-300">ERP</span>
          </h2>
          <p className="italic text-lime-200/90 text-lg font-medium mb-5">
            Integrated Business Management System
          </p>

          <p className="text-emerald-100/85 text-sm leading-relaxed mb-8 max-w-md">
            Kelola bisnis lebih mudah dalam <em className="text-lime-200 not-italic font-semibold">satu sistem</em> —
            kas harian, akuntansi, produk, marketplace, dan sales dalam satu dashboard.
          </p>

          <div className="bg-emerald-900/40 backdrop-blur border border-emerald-700/50 rounded-2xl p-5 max-w-md">
            <div className="text-[10px] uppercase tracking-widest text-lime-200/80 font-semibold mb-3">
              Modul Terintegrasi
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-2 text-sm font-medium text-emerald-50">
              {['Kas Harian', 'Akuntansi', 'Produk', 'Marketplace', 'Sales'].map((f, i) => (
                <React.Fragment key={f}>
                  {i > 0 && <span className="text-lime-300/60">·</span>}
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lime-300" strokeWidth={2.5} />
                    {f}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-8 text-[11px] tracking-widest uppercase text-emerald-300/70">
            © 2026 MAINBULUTANGKIS ERP · Integrated Business Management System
          </div>
        </div>
      </aside>

      {/* ============ RIGHT: form panel (first on mobile) ============ */}
      <section className="bg-white w-full lg:w-1/2 lg:min-h-screen flex items-center justify-center p-6 md:p-10 order-1 lg:order-2">
        <div className="w-full max-w-sm animate-rise">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-emerald-800 tracking-tight">
              MAINBULUTANGKIS ERP
            </h1>
            <p className="text-xs italic text-stone-500 mt-2">
              Integrated Business Management System
            </p>
            <p className="text-sm text-stone-600 mt-4 font-medium">
              Masuk untuk mulai kelola bisnis Anda
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
            {/* Username */}
            <div>
              <Label className="text-sm font-semibold text-stone-800 mb-1.5 flex items-center justify-center gap-1.5">
                <UserIcon className="w-4 h-4 text-emerald-800" /> User ID
              </Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                data-testid="login-username"
                placeholder="Masukkan User ID Anda"
                className="bg-emerald-50/50 border-emerald-100 focus-visible:ring-emerald-500 h-11 text-base text-center"
              />
            </div>

            {/* Password */}
            <div>
              <Label className="text-sm font-semibold text-stone-800 mb-1.5 flex items-center justify-center gap-1.5">
                <Lock className="w-4 h-4 text-emerald-800" /> Password
              </Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  data-testid="login-password"
                  placeholder="••••••"
                  className="bg-emerald-50/50 border-emerald-100 focus-visible:ring-emerald-500 h-11 text-base pr-11 text-center"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-emerald-800 transition-colors"
                  data-testid="login-toggle-password"
                  aria-label={showPw ? 'Sembunyikan' : 'Lihat'}
                >
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {password !== trimmedPw && password.length > 0 && (
                <p className="text-[11px] text-amber-700 text-center mt-1.5" data-testid="login-warn-space">
                  ⚠ Password Anda memiliki spasi di awal/akhir — akan dihapus otomatis
                </p>
              )}
            </div>

            {/* Login button */}
            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-11 bg-emerald-800 hover:bg-emerald-900 text-white text-base font-bold tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              {busy ? 'MEMPROSES…' : 'MASUK'}
            </Button>

            {/* Divider */}
            <div className="relative py-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[11px] uppercase tracking-widest text-stone-400 font-semibold">
                  atau
                </span>
              </div>
            </div>

            <div className="text-center text-sm text-stone-600 leading-relaxed">
              Belum punya akun kasir?{' '}
              <span className="text-emerald-800 font-medium">
                Hubungi owner untuk pembuatan akun.
              </span>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-500 pt-1">
              <HelpCircle className="w-3.5 h-3.5" />
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); toast.info('Hubungi admin/owner untuk reset password'); }}
                className="hover:text-emerald-800 hover:underline transition-colors"
                data-testid="login-forgot"
              >
                Panduan Pemakaian
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
